// src/store/tasksSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tasksAPI } from '../services/api';

// ── Thunks ────────────────────────────────────────────────────────────────────
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await tasksAPI.list(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch tasks');
    }
  }
);

export const fetchTaskStats = createAsyncThunk(
  'tasks/fetchStats',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await tasksAPI.stats(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch stats');
    }
  }
);

export const fetchSingleTask = createAsyncThunk(
  'tasks/fetchSingle',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await tasksAPI.get(id);
      return data.entry;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch task');
    }
  }
);

export const editTask = createAsyncThunk(
  'tasks/edit',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await tasksAPI.edit(id, data);
      return res.data.taskEntry;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Update failed');
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/delete',
  async (id, { rejectWithValue }) => {
    try {
      await tasksAPI.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Delete failed');
    }
  }
);

export const bulkDeleteTasks = createAsyncThunk(
  'tasks/bulkDelete',
  async (ids, { rejectWithValue }) => {
    try {
      await tasksAPI.bulkDelete(ids);
      return ids;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Bulk delete failed');
    }
  }
);

export const restoreTask = createAsyncThunk(
  'tasks/restore',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await tasksAPI.restore(id);
      return data.taskEntry;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Restore failed');
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const tasksSlice = createSlice({
  name: 'tasks',
  initialState: {
    entries:    [],
    pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
    stats:      null,
    selected:   null,
    loading:    false,
    statsLoading: false,
    error:      null,
    actionLoading: false,
  },
  reducers: {
    clearSelected:   (s) => { s.selected = null; },
    clearTasksError: (s) => { s.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // fetchTasks
      .addCase(fetchTasks.pending,   (s) => { s.loading = true; s.error = null; })
      .addCase(fetchTasks.fulfilled, (s, { payload }) => {
        s.loading    = false;
        s.entries    = payload.entries;
        s.pagination = payload.pagination;
      })
      .addCase(fetchTasks.rejected,  (s, { payload }) => { s.loading = false; s.error = payload; })

      // fetchTaskStats
      .addCase(fetchTaskStats.pending,   (s) => { s.statsLoading = true; })
      .addCase(fetchTaskStats.fulfilled, (s, { payload }) => {
        s.statsLoading = false;
        s.stats        = payload;
      })
      .addCase(fetchTaskStats.rejected,  (s) => { s.statsLoading = false; })

      // fetchSingleTask
      .addCase(fetchSingleTask.fulfilled, (s, { payload }) => { s.selected = payload; })

      // editTask
      .addCase(editTask.pending,   (s) => { s.actionLoading = true; })
      .addCase(editTask.fulfilled, (s, { payload }) => {
        s.actionLoading = false;
        const idx = s.entries.findIndex((e) => e._id === payload._id);
        if (idx !== -1) s.entries[idx] = payload;
        if (s.selected?._id === payload._id) s.selected = payload;
      })
      .addCase(editTask.rejected, (s, { payload }) => { s.actionLoading = false; s.error = payload; })

      // deleteTask
      .addCase(deleteTask.fulfilled, (s, { payload }) => {
        s.entries = s.entries.filter((e) => e._id !== payload);
        s.pagination.total = Math.max(0, s.pagination.total - 1);
      })

      // bulkDeleteTasks
      .addCase(bulkDeleteTasks.fulfilled, (s, { payload }) => {
        s.entries = s.entries.filter((e) => !payload.includes(e._id));
        s.pagination.total = Math.max(0, s.pagination.total - payload.length);
      })

      // restoreTask
      .addCase(restoreTask.fulfilled, (s, { payload }) => {
        const idx = s.entries.findIndex((e) => e._id === payload._id);
        if (idx !== -1) s.entries[idx] = payload;
      });
  },
});

export const { clearSelected, clearTasksError } = tasksSlice.actions;
export default tasksSlice.reducer;

export const selectTasks      = (s) => s.tasks.entries;
export const selectPagination = (s) => s.tasks.pagination;
export const selectTaskStats  = (s) => s.tasks.stats;
export const selectSelected   = (s) => s.tasks.selected;
export const selectLoading    = (s) => s.tasks.loading;
export const selectStatsLoading = (s) => s.tasks.statsLoading;
export const selectActionLoading = (s) => s.tasks.actionLoading;
